import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Grid,
    Divider,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Avatar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { format } from "date-fns";
import { formatToUIDateTime, formatToUIDateTimeLong } from "../utils/dateUtils";
import clinicalNoteService from "../services/clinicalNoteService";
import ResidentProgressNoteForm from "./ResidentProgressNoteForm";

import { useAuth } from "../contexts/AuthContext";

interface ProgressNotesSectionProps {
    residentId: string | null;
    residentName?: string;
    onSaveSuccess?: () => void;
    readOnly?: boolean;
}

const robustParse = (d: any): number => {
    if (!d) return 0;
    const date = new Date(d);
    return date.getTime() || 0;
};

const robustFormat = (d: any): string => {
    if (!d) return "Unknown";
    return formatToUIDateTimeLong(d);
};

const ProgressNotesSection: React.FC<ProgressNotesSectionProps> = ({ residentId, residentName, onSaveSuccess, readOnly = false }) => {
    const [notes, setNotes] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editingNote, setEditingNote] = useState<any | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const { user: currentUser } = useAuth();

    const isOnlyCaregiver = useMemo(() => {
        if (!currentUser?.role) return false;
        const roleStr = String(currentUser.role).toUpperCase().replace(/^ROLE_/, "").trim();
        return roleStr.includes("CAREGIVER") || roleStr === "NURSE";
    }, [currentUser]);

    const isSuperAdmin = useMemo(() => {
        if (!currentUser?.role) return false;
        const roleStr = String(currentUser.role).toUpperCase().replace(/^ROLE_/, "").trim();
        return roleStr === "SUPER_ADMIN";
    }, [currentUser]);

    const fetchNotes = useCallback(async () => {
        if (!residentId) return;
        setLoading(true);
        setError(null);
        try {
            const data = await clinicalNoteService.getProgressNotesByResident(residentId);
            setNotes(data || []);
        } catch (err: any) {
            console.error("Failed to fetch progress notes", err);
            setError(err?.response?.data?.message || "Failed to load progress notes.");
        } finally {
            setLoading(false);
        }
    }, [residentId]);

    useEffect(() => {
        fetchNotes();
    }, [fetchNotes]);

    const handleDeleteNote = (noteId: string) => {
        if (!noteId) return;
        setNoteToDelete(noteId);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!noteToDelete) return;

        setIsDeleting(true);
        try {
            await clinicalNoteService.deleteProgressNote(noteToDelete);
            setDeleteDialogOpen(false);
            setNoteToDelete(null);
            fetchNotes();
            if (onSaveSuccess) onSaveSuccess();
        } catch (err: any) {
            console.error("Failed to delete note", err);
            setError(err?.response?.data?.message || "Failed to delete progress note.");
        } finally {
            setIsDeleting(false);
        }
    };

    const sortedNotes = useMemo(() => {
        return [...notes].sort((a: any, b: any) => {
            const dateA = robustParse(a.dateTime || a.noteDate || a.createdAt);
            const dateB = robustParse(b.dateTime || b.noteDate || b.createdAt);
            return dateB - dateA;
        });
    }, [notes]);

    return (
        <>
            <Box>
                <Grid container spacing={3} alignItems="stretch">
                    {/* Form Column */}
                    {!readOnly && (
                        <Grid item xs={12} md={5} lg={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Paper sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                    {editingNote ? "Update Progress Note" : "Add New Progress Note"}
                                </Typography>
                                <Divider sx={{ mb: 2, mx: -2 }} />

                                <Box sx={{ flexGrow: 1 }}>
                                    <ResidentProgressNoteForm
                                        residentId={residentId}
                                        initialData={editingNote}
                                        latestNoteContent={sortedNotes.length > 0 ? (sortedNotes[0].note || sortedNotes[0].content || "") : ""}
                                        skipFetch={true}
                                        onCancel={editingNote ? () => setEditingNote(null) : undefined}
                                        onSuccess={() => {
                                            setEditingNote(null);
                                            fetchNotes();
                                            if (onSaveSuccess) onSaveSuccess();
                                        }}
                                    />
                                </Box>
                            </Paper>
                        </Grid>
                    )}

                    {/* History Column */}
                    <Grid item xs={12} md={readOnly ? 12 : 7} lg={readOnly ? 12 : 8} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Paper sx={{ p: 2, flexGrow: 1, minHeight: 300, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                Progress Notes History
                            </Typography>
                            <Divider sx={{ mb: 2, mx: -2 }} />

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}
                            {loading ? (
                                <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
                                    <CircularProgress />
                                </Box>
                            ) : notes.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No notes found.
                                </Typography>
                            ) : (
                                <TableContainer sx={{ flexGrow: 1, overflowX: "auto" }}>
                                    <Table size="small" sx={{ minWidth: 800 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Date & Time</TableCell>
                                                <TableCell sx={{ fontWeight: "bold", minWidth: 250 }}>Note</TableCell>
                                                <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Created By</TableCell>
                                                <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Created On</TableCell>
                                                <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Updated On</TableCell>
                                                {!readOnly && (
                                                    <TableCell align="center" sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Actions</TableCell>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>

                                            {sortedNotes
                                                .map((n: any, index: number) => {
                                                    const noteDate = new Date(n.dateTime || n.noteDate || n.createdAt);
                                                    const today = new Date();
                                                    today.setHours(0, 0, 0, 0);
                                                    const isPrevious = noteDate.getTime() < today.getTime();
                                                    const showEdit = !readOnly && !(isOnlyCaregiver && isPrevious);
                                                    // Adjust fields based on API response
                                                    // The POST used "dateTime", "note"
                                                    // Prefer the actual note timestamp on the record.
                                                    // Some backends update `createdAt/createdOn` on edit, which would make the UI look like the note date changed.
                                                    const displayDateStr = n.dateTime || n.noteDate || n.createdAt || n.createdOn;
                                                    const createdOnStr = n.createdOn || n.createdAt;
                                                    let displayNote = n.note || n.content;

                                                    const formatDate = (dateStr: string) => {
                                                        return formatToUIDateTimeLong(dateStr);
                                                    };

                                                    const formattedDate = robustFormat(displayDateStr);
                                                    const formattedCreatedOn = formatToUIDateTimeLong(createdOnStr);

                                                    return (
                                                        <TableRow key={n.id || index}>
                                                            <TableCell sx={{ whiteSpace: "nowrap", minWidth: 160 }}>
                                                                {formattedDate}
                                                            </TableCell>
                                                            <TableCell sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", minWidth: 250 }}>
                                                                {displayNote || "-"}
                                                            </TableCell>
                                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                {(() => {
                                                                    if (n.createdByName) return n.createdByName;
                                                                    if (n.authorName) return n.authorName;

                                                                    const creatorId = n.createdBy || n.authorId;
                                                                    if (!creatorId) return "-";


                                                                    // Check if it matches current user
                                                                    if (
                                                                        currentUser &&
                                                                        (String(currentUser.id).toLowerCase() === String(creatorId).toLowerCase() ||
                                                                            String(currentUser.username).toLowerCase() === String(creatorId).toLowerCase())
                                                                    ) {
                                                                        return currentUser.name || currentUser.username;
                                                                    }

                                                                    return creatorId || "-";
                                                                })()}
                                                            </TableCell>
                                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                {formattedCreatedOn}
                                                            </TableCell>
                                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                {(() => {
                                                                    const d = n.updatedAt || n.updated_at || n.updatedOn || n.lastModifiedDate;
                                                                    return d ? formatToUIDateTimeLong(d) : "-";
                                                                })()}
                                                            </TableCell>
                                                            {!readOnly && (
                                                                <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                                                                    {showEdit ? (
                                                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                                            <Tooltip title="Edit Note">
                                                                                <IconButton size="small" color="primary" onClick={() => setEditingNote(n)}>
                                                                                    <EditIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                            {isSuperAdmin && (
                                                                                <Tooltip title="Delete Note">
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        color="error"
                                                                                        onClick={() => handleDeleteNote(n.id)}
                                                                                        disabled={loading}
                                                                                    >
                                                                                        <DeleteIcon fontSize="small" />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            )}
                                                                        </Box>
                                                                    ) : (
                                                                        "-"
                                                                    )}
                                                                </TableCell>
                                                            )}
                                                        </TableRow>
                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => !isDeleting && setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: "20px" } }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1, pt: 3, px: 3 }}>
                    <Avatar sx={{ bgcolor: "#FEE2E2", color: "#DC2626", borderRadius: "10px" }}><DeleteIcon /></Avatar>
                    <Typography fontWeight={800} fontSize="1rem">Confirm Delete</Typography>
                </DialogTitle>
                <DialogContent sx={{ px: 3, pb: 3, pt: 1 }}>
                    <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6 }}>
                        Are you sure you want to delete this progress note?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={isDeleting}
                        sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: "#475569" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        variant="contained"
                        disabled={isDeleting}
                        sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#DC2626", "&:hover": { bgcolor: "#B91C1C" }, px: 3 }}
                        startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : undefined}
                    >
                        {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ProgressNotesSection;
